// Operations on the instances of a type, as computations: each reads the scans (InstScan) and returns
// the ops to commit per scan (by scan id), and a note on how it went. Committing them (as one action),
// the worker pool and the progress bar are the page's: both the editor and the object page run these.
import { type Rigid, Grid, apply, compose, identity, inverse, isIdentity, yawAbout } from '../align.ts';
import type { V3 } from '../math.ts';
import { CLS, DELETED, MAX_ID, type Cls, type TypeBox, nextId } from './log.ts';
import type { OpBody } from './session.ts';
import { type FrameFit, type Fitted, centroid, nextInstanceName } from './instances.ts';
import { densityFit as densityFitRun, type DInst, type Task, type TaskResult } from './density.ts';
import { type InstScan, type ObjInfo, instanceBox, isFitted, idsNamed, instancesOf, instanceIx, instanceParts, clusters, masterOf, masterPart, place, placer, objectPts, hasMaster } from './overlay.ts';

/** Run a task on the pool; transfer hands its arrays over instead of copying them (they are spent). */
export type Exec = (t: Task, transfer?: boolean) => Promise<TaskResult>;

/** What an operation works on: the scans (world copies current), the type, and the page's worker pool. */
export interface InstCtx {
  scans: InstScan[];
  type: string;
  info: ObjInfo;
  exec: Exec;
  /** Workers in the pool. */
  K: number;
  progress: (text: string, frac: number) => void;
}

/** Ops per scan id, and a note for the page. Empty bodies: nothing to commit. */
export interface OpOut { bodies: Map<string, OpBody[]>; note: string; fits?: Map<string, Rigid> }

const push = (m: Map<string, OpBody[]>, scan: string, ...b: OpBody[]) => { (m.get(scan) ?? m.set(scan, []).get(scan)!).push(...b); };

/** What the instances are fitted onto, for messages. */
export const masterLabel = (scans: InstScan[], type: string, info: ObjInfo) => (hasMaster(scans, type) ? `the ${type} master` : `"${instancesOf(info, type)[0]}"`);

/** New fits for some instances: object.of (with F) in every scan that has them. */
export function fitBodies(scans: InstScan[], type: string, fits: Map<string, Rigid>, out = new Map<string, OpBody[]>()) {
  for (const s of scans) for (const [n, F] of fits) {
    const id = idsNamed(s.state.objects, n)[0];
    if (id) push(out, s.scan, { op: 'object.of', obj: id, of: type, F } as OpBody);
  }
  return out;
}

/**
 * Fit instances onto the master (each from scratch: 8 starting turns, then ICP). Without a master set the
 * first instance is the master: it stays put and the others are fitted onto it. With one, it is fitted
 * like the others (onto the rest of the master), so it can't be left behind.
 */
export async function fitInstances(ctx: InstCtx, names: string[]): Promise<OpOut> {
  const { scans, type, info } = ctx, all = instancesOf(info, type), ref = all[0], explicit = hasMaster(scans, type), onto = masterLabel(scans, type, info);
  if (!explicit) names = names.filter(n => n !== ref);
  if (!names.length) return { bodies: new Map(), note: `"${ref}" is the master until points are put In master: the others are fitted onto it` };
  const m = masterOf(scans, type, all, info), raw = objectPts(scans, names, null), notes: string[] = [];
  const shared = explicit ? null : masterPart(m, -1);   // without a master set: the first instance, the same for all
  const tasks: { name: string; task: Task }[] = [];
  for (const n of names) {
    const p = raw.get(n)!;
    if (p.xyz.length < 300) { notes.push(`${n}: too few points`); continue; }
    const t = shared ?? masterPart(m, all.indexOf(n));   // a master leaves out the instance's own points
    tasks.push({ name: n, task: { kind: 'onto', xyz: t.xyz, nrm: t.nrm, src: p.xyz } });
  }
  if (!tasks.length) return { bodies: new Map(), note: notes.join('; ') || 'nothing to fit' };
  let done = 0;
  const res = await Promise.all(tasks.map(async t => {
    const f = await ctx.exec(t.task, !shared) as Fitted | null;
    ctx.progress(`${t.name}: ${f ? Math.round(100 * f.share) + '%' : 'no fit'}`, ++done / tasks.length);
    return [t.name, f] as const;
  }));
  const fits = new Map<string, Rigid>();
  for (const [n, f] of res) if (f) { fits.set(n, f.F); notes.push(`${n} ${Math.round(100 * f.share)}%`); } else notes.push(`${n}: the master has too few points apart from it`);
  return { bodies: fitBodies(scans, type, fits), note: `fitted onto ${onto} (points within 3 cm): ${notes.join(', ')}`, fits };
}

/**
 * Fit each instance's points onto the master (less that instance's own points) frame by frame, as a
 * correction W of the instance's points from that frame: a better copy of the object, not a better
 * scan. The scan isn't moved: a frame can see several instances, and each is corrected on its own.
 * together: each frame is fitted onto the master and the instance's points from its other frames (one
 * task per frame), so its frames line up with each other too; the first instance without a master set
 * is then fitted onto its own other frames.
 */
export async function fitInstanceFrames(ctx: InstCtx, names: string[], together = false): Promise<OpOut> {
  const { scans, type, info } = ctx, all = instancesOf(info, type), explicit = hasMaster(scans, type);
  if (!explicit && !together) names = names.filter(n => n !== all[0]);   // without a master set, the first instance is the master
  if (!names.length) return { bodies: new Map(), note: `"${all[0]}" is the master until points are put In master` };
  if (names.some(n => n !== all[0] && isIdentity(info.get(n)?.F ?? identity()))) return { bodies: new Map(), note: 'fit the instances first (Auto / Fit all), then their frames' };
  const m = masterOf(scans, type, all, info), notes: string[] = [];
  type Where = { s: InstScan; id: number; frame: number; p: number[]; q: number[] };
  const work: { name: string; list: { key: string; xyz: Float32Array; nrm: Float32Array }[]; where: Map<string, Where>; c: Map<string, V3> }[] = [];
  for (const n of names) {
    // its points by scan and frame, as the overlay shows them (F · W · P)
    const where = new Map<string, Where>(), one = new Float32Array(3), on = new Float32Array(3);
    for (const s of scans) {
      const st = s.state, ids = new Set(idsNamed(st.objects, n));
      if (!s.frame || !ids.size) continue;
      const pl = placer(s, info);
      for (let v = 0; v < s.nv; v++) {
        const p = s.pidOfV[v], id = st.obj[p], f = s.frame[v];
        if (!f || !ids.has(id) || st.flags[p] & DELETED) continue;
        place(pl(id, v), s.P, s.N, v, one, on, 0);
        const key = `${s.scan}|${f}`, w = where.get(key) ?? where.set(key, { s, id, frame: f, p: [], q: [] }).get(key)!;
        w.p.push(one[0], one[1], one[2]); w.q.push(on[0], on[1], on[2]);
      }
    }
    const list = [...where].map(([key, w]) => ({ key, xyz: new Float32Array(w.p), nrm: new Float32Array(w.q) }));
    if (list.length) work.push({ name: n, list, where, c: new Map(list.map(l => [l.key, centroid(l.xyz)])) });
  }
  const total = work.reduce((a, w) => a + w.list.length, 0);
  if (!total) return { bodies: new Map(), note: 'those instances have no points with frame ids' };
  let done = 0;
  const out = await Promise.all(work.map(async w => {
    const t = masterPart(m, all.indexOf(w.name));   // the master less this instance's own points
    if (!together) {
      const fits = await ctx.exec({ kind: 'fit', xyz: t.xyz, nrm: t.nrm, list: w.list.map(l => ({ key: l.key, xyz: l.xyz })) }, true) as FrameFit<string>[] | null;
      done += w.list.length;
      ctx.progress(`${w.name} done`, done / total);
      if (!fits) notes.push(`${w.name}: the master has too few points apart from it`);
      return { w, fits: fits ?? [] };
    }
    // each frame onto the master and this instance's other frames
    const fits = await Promise.all(w.list.map(async l => {
      const rest = w.list.filter(o => o !== l), n = t.xyz.length + rest.reduce((a, o) => a + o.xyz.length, 0);
      const xyz = new Float32Array(n), nrm = new Float32Array(n);
      xyz.set(t.xyz); nrm.set(t.nrm);
      let at = t.xyz.length;
      for (const o of rest) { xyz.set(o.xyz, at); nrm.set(o.nrm, at); at += o.xyz.length; }
      const r = await ctx.exec({ kind: 'fit', xyz, nrm, list: [{ key: l.key, xyz: l.xyz.slice() }] }, true) as FrameFit<string>[] | null;
      ctx.progress(`${w.name}: frame ${l.key}`, ++done / total);
      return r?.[0] ?? null;
    }));
    if (fits.every(f => !f)) notes.push(`${w.name}: too few points to fit its frames onto`);
    return { w, fits: fits.filter((f): f is FrameFit<string> => !!f) };
  }));
  // per scan and object, its whole set of corrections, with W' = F⁻¹ · G · F · W for each corrected frame
  const byScan = new Map<InstScan, Map<number, Record<string, Rigid>>>(), cm: number[] = [];
  let moved = 0;
  const skip: Record<string, number> = {};
  for (const { w, fits } of out) for (const r of fits) {
    if (r.skip) { skip[r.skip] = (skip[r.skip] ?? 0) + 1; continue; }
    moved++;
    const x = w.where.get(r.key)!, o = x.s.state.objects.get(x.id)!, F = info.get(w.name)?.F ?? o.F ?? identity();
    const per = byScan.get(x.s) ?? byScan.set(x.s, new Map()).get(x.s)!;
    const D = per.get(x.id) ?? per.set(x.id, Object.fromEntries([...(o.W ?? [])].map(([f, d]) => [`f:${f}`, d]))).get(x.id)!;
    D[`f:${x.frame}`] = compose(inverse(F), compose(r.W, compose(F, o.W?.get(x.frame) ?? identity())));
    const c = w.c.get(r.key)!, q = apply(r.W, c);
    cm.push(100 * Math.hypot(q[0] - c[0], q[1] - c[1], q[2] - c[2]));
  }
  const bodies = new Map<string, OpBody[]>();
  for (const [s, per] of byScan) for (const [id, D] of per) push(bodies, s.scan, { op: 'object.frames', obj: id, D } as OpBody);
  const note = `${moved} of ${total} instance frames corrected` + (cm.length ? ` (median ${cm.sort((a, b) => a - b)[cm.length >> 1].toFixed(1)} cm)` : '') +
    `; left alone: ${skip['few points'] ?? 0} with < 150 points, ${skip['no better'] ?? 0} no better, ${skip['too far'] ?? 0} > 15 cm / 8°` + (notes.length ? `; ${notes.join('; ')}` : '');
  return { bodies, note };
}

/**
 * Fit one instance from scratch: the whole of it onto the master (fitInstances; not the first instance
 * without a master set, which is the master), then each of its frames onto the master and its other
 * frames (fitInstanceFrames, together), placed with the new fit. One set of ops: one Undo.
 */
export async function fitInstanceAll(ctx: InstCtx, name: string): Promise<OpOut> {
  const { scans, type, info } = ctx, all = instancesOf(info, type), isRef = !hasMaster(scans, type) && name === all[0];
  const whole = isRef ? null : await fitInstances(ctx, [name]), F = whole?.fits?.get(name);
  const info2: ObjInfo = new Map(info);
  if (F) info2.set(name, { ...info.get(name)!, F });
  const frames = await fitInstanceFrames({ ...ctx, info: info2 }, [name], true);
  const bodies = new Map<string, OpBody[]>();
  for (const r of [whole, frames]) if (r) for (const [scan, b] of r.bodies) push(bodies, scan, ...b);
  const note = [isRef ? `"${name}" is the master (none set): only its frames are fitted, onto each other` : whole!.note, frames.note].join('; ');
  return { bodies, note };
}

/**
 * Density fit: fit every instance, then its frames, onto where the instances agree (density.ts). All of
 * them make the consensus; only `only` (default: all) get new fits and frame corrections committed.
 */
export async function densityFit(ctx: InstCtx, rounds = 3, only?: Set<string>): Promise<OpOut> {
  const { scans, type, info } = ctx, all = instancesOf(info, type), names = new Set(all), t0 = Date.now();
  // each instance's points (world, before W and F), by scan|frame, with its current corrections
  const acc = new Map<string, Map<string, { W: Rigid | null; fit: boolean; p: number[]; n: number[] }>>();
  for (const s of scans) {
    const st = s.state;
    for (let v = 0; v < s.nv; v++) {
      const p = s.pidOfV[v], id = st.obj[p];
      if (!id || st.flags[p] & DELETED) continue;
      const o = st.objects.get(id)!;
      if (o.of !== type || !names.has(o.name)) continue;
      const f = s.frame?.[v] ?? 0, key = `${s.scan}|${f}`, byKey = acc.get(o.name) ?? acc.set(o.name, new Map()).get(o.name)!;
      const g = byKey.get(key) ?? byKey.set(key, { W: f ? o.W?.get(f) ?? null : null, fit: f > 0, p: [], n: [] }).get(key)!;
      g.p.push(s.P[3 * v], s.P[3 * v + 1], s.P[3 * v + 2]); g.n.push(s.N[3 * v], s.N[3 * v + 1], s.N[3 * v + 2]);
    }
  }
  const insts: DInst[] = all.filter(n => acc.has(n)).map(name => ({
    name, F: info.get(name)?.F ?? identity(), anchor: false,   // the consensus needs no fixed instance
    frames: [...acc.get(name)!].map(([key, g]) => ({ key, W: g.W, fit: g.fit, xyz: new Float32Array(g.p), nrm: new Float32Array(g.n) })),
  }));
  if (insts.length < 2) return { bodies: new Map(), note: `density fit needs at least two ${type} instances with points` };
  const r = await densityFitRun(insts, rounds, ctx.progress, (t: Task) => ctx.exec(t, t.kind !== 'support'), ctx.K);
  const keep = (n: string) => !only || only.has(n);
  // the new fits, then (per scan and object) its whole set of corrections with the new ones in
  const bodies = fitBodies(scans, type, new Map(Object.entries(r.F).filter(([n]) => keep(n))));
  const byId = new Map(scans.map(s => [s.scan, s]));
  const by = new Map<InstScan, Map<number, Record<string, Rigid>>>();
  for (const [name, ks] of Object.entries(r.W)) {
    if (!keep(name)) continue;
    for (const [key, W] of Object.entries(ks)) {
      const [scan, f] = key.split('|'), s = byId.get(scan);
      const id = s && idsNamed(s.state.objects, name)[0];
      if (!s || !id || !+f) continue;
      const per = by.get(s) ?? by.set(s, new Map()).get(s)!, o = s.state.objects.get(id)!;
      const D = per.get(id) ?? per.set(id, Object.fromEntries([...(o.W ?? [])].map(([k, d]) => [`f:${k}`, d]))).get(id)!;
      D[`f:${f}`] = W;
    }
  }
  for (const [s, per] of by) for (const [id, D] of per) push(bodies, s.scan, { op: 'object.frames', obj: id, D } as OpBody);
  return {
    bodies,
    note: `density fit (${rounds} rounds, ${ctx.K} workers, ${Math.round((Date.now() - t0) / 1000)} s): ${r.ni} instance fits and ${r.nf} frame corrections; ` +
      `agreement ${Math.round(100 * r.before)}% → ${Math.round(100 * r.after)}% (the mean share of the other instances with a point within 3 cm of each point)`,
  };
}

/** Per-point support of overlay points (instances.ts support), split over the pool. */
export async function supportOf(exec: Exec, K: number, xyz: Float32Array, inst: Int32Array): Promise<Uint16Array> {
  const n = inst.length, cuts = Array.from({ length: K + 1 }, (_, i) => Math.round((i * n) / K));
  const parts = await Promise.all(cuts.slice(0, -1).map((from, i) => exec({ kind: 'support', xyz, inst, from, to: cuts[i + 1] }) as Promise<Uint16Array>));
  const sup = new Uint16Array(n);
  parts.forEach((q, i) => sup.set(q, cuts[i]));
  return sup;
}

/** How far (m) from an instance's points Absorb looks for points to add to it. */
export const ABSORB_REACH = 0.5;

/**
 * Absorb: points of a class that are in no object join an instance where they belong. Each goes with the
 * instance (of `into`) whose points are nearest it in its scan (within ABSORB_REACH), is placed in the
 * overlay as that instance is (its frame correction and fit), and is taken if it lands within 3 cm of
 * `dense` (overlay points enough instances agree with).
 */
export function absorb(ctx: Omit<InstCtx, 'exec' | 'K' | 'progress'>, cls: string, into: Set<string>, dense: Float32Array, userClasses: (s: InstScan) => Map<number, { name: string }>): OpOut & { total: number } {
  const { scans, type, info } = ctx, names = instancesOf(info, type);
  const dg = new Grid(dense, 0.03), q = new Float32Array(3), qn = new Float32Array(3);
  const bodies = new Map<string, OpBody[]>(), per = new Map<string, number>();
  const R = ABSORB_REACH, cell = (x: number) => Math.floor(x / R) + 8192, cellKey = (i: number, j: number, k: number) => (i * 16384 + j) * 16384 + k;
  for (const s of scans) {
    const st = s.state;
    const id = CLS.indexOf(cls as Cls) >= 0 ? CLS.indexOf(cls as Cls) : [...userClasses(s)].find(([, c]) => c.name === cls)?.[0];
    if (id === undefined) continue;
    // the instances' points here (world), which object each is in, and the cells near them (a quick no)
    const kv = instanceIx(s, type, names), ip: number[] = [], io: number[] = [], own = new Set<number>(), near = new Set<number>();
    for (let v = 0; v < s.nv; v++) {
      if (kv[v] < 0 || !into.has(names[kv[v]])) continue;
      const x = s.P[3 * v], y = s.P[3 * v + 1], z = s.P[3 * v + 2];
      ip.push(x, y, z); io.push(st.obj[s.pidOfV[v]]);
      own.add(cellKey(cell(x), cell(y), cell(z)));
    }
    if (!io.length) continue;
    for (const k of own) {
      const i = Math.floor(k / 16384 / 16384), j = Math.floor(k / 16384) % 16384, l = k % 16384;
      for (let a = -1; a <= 1; a++) for (let b = -1; b <= 1; b++) for (let c = -1; c <= 1; c++) near.add(cellKey(i + a, j + b, l + c));
    }
    const g = new Grid(new Float32Array(ip), 0.1), pl = placer(s, info), masks = new Map<number, Uint8Array>();
    for (let v = 0; v < s.nv; v++) {
      const p = s.pidOfV[v], f = st.flags[p];
      if (st.obj[p] || f & DELETED || f >> 1 !== id) continue;
      const x = s.P[3 * v], y = s.P[3 * v + 1], z = s.P[3 * v + 2];
      if (!near.has(cellKey(cell(x), cell(y), cell(z)))) continue;
      const j = g.nearest(x, y, z, R);
      if (j < 0) continue;
      const o = io[j];
      place(pl(o, v), s.P, s.N, v, q, qn, 0);
      if (dg.nearest(q[0], q[1], q[2], 0.03) < 0) continue;
      (masks.get(o) ?? masks.set(o, new Uint8Array(s.nv)).get(o)!)[v] = 1;
      const nm = st.objects.get(o)!.name;
      per.set(nm, (per.get(nm) ?? 0) + 1);
    }
    for (const [o, m] of masks) push(bodies, s.scan, { op: 'object.assign', obj: o, set: s.toSet(m) } as OpBody);
  }
  const total = [...per.values()].reduce((a, b) => a + b, 0);
  return { bodies, total, note: total ? `${total.toLocaleString()} point(s) added (${[...per].sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true })).map(([n, k]) => `${n} ${k.toLocaleString()}`).join(', ')})` : '' };
}

/** Clear the frame corrections of some instances. */
export function resetFrames(scans: InstScan[], names: Iterable<string>): Map<string, OpBody[]> {
  const want = new Set(names), bodies = new Map<string, OpBody[]>();
  for (const s of scans) for (const [id, o] of s.state.objects) if (want.has(o.name) && o.W) push(bodies, s.scan, { op: 'object.frames', obj: id, D: {} } as OpBody);
  return bodies;
}

/** Turn instances by a yaw about each one's centre in the overlay (and shift them): their new fits. */
export function turnFits(scans: InstScan[], info: ObjInfo, names: string[], deg: number, d: V3 = [0, 0, 0]): Map<string, Rigid> {
  const pts = objectPts(scans, names, info), fits = new Map<string, Rigid>();
  for (const n of names) {
    const F = info.get(n)?.F ?? identity(), p = pts.get(n)!;
    if (!p.xyz.length) continue;
    const W = yawAbout((deg * Math.PI) / 180, centroid(p.xyz), d);   // about its centre in the overlay
    fits.set(n, compose(W, F));
  }
  return fits;
}

/**
 * Untangle: instances whose parts in different scans are in different places (one name given to
 * different objects): the biggest group keeps the name, each other group becomes a new instance (a name
 * unique in `taken`). Renamed parts start unfitted (object.of without F), so they need fitting again.
 */
export function untangle(scans: InstScan[], type: string, names: string[], taken: Set<string>): OpOut & { made: string[] } {
  const parts = instanceParts(scans, type, names), bodies = new Map<string, OpBody[]>(), made: string[] = [];
  for (const [, ps] of parts) for (const g of clusters(ps).slice(1)) {
    const name = nextInstanceName(type, taken);
    taken.add(name); made.push(name);
    for (const p of g) push(bodies, p.s.scan, { op: 'object.meta', obj: p.id, name } as OpBody, { op: 'object.of', obj: p.id, of: type } as OpBody);
  }
  return { bodies, made, note: made.length ? `${made.length} new instance(s): ${made.join(', ')}` : `every ${type} instance is in one place in all the scans` };
}

/** Is p inside the box with these corners (see boxCorners)? */
function inCorners(c: V3[], p: V3) {
  const o = c[0];
  return [1, 2, 4].every(i => {
    const a = [c[i][0] - o[0], c[i][1] - o[1], c[i][2] - o[2]], L = a[0] * a[0] + a[1] * a[1] + a[2] * a[2];
    const t = (p[0] - o[0]) * a[0] + (p[1] - o[1]) * a[1] + (p[2] - o[2]) * a[2];
    return t >= 0 && t <= L;
  });
}

/**
 * The other instances of name's type at its place: the centre of their points (not deleted, in the world) inside
 * name's box, or name's inside theirs (each its type's box placed by its own fit; an unfitted one has none). Most likely the same object,
 * named twice (from two scans, say).
 */
export function nearInstances(scans: InstScan[], info: ObjInfo, box: TypeBox, name: string): string[] {
  const type = info.get(name)?.of;
  if (!type) return [];
  const names = instancesOf(info, type), parts = instanceParts(scans, type, names), at = new Map<string, V3>();
  for (const [n, ps] of parts) {
    const k = ps.reduce((a, q) => a + q.n, 0);
    if (k) at.set(n, [0, 1, 2].map(d => ps.reduce((a, q) => a + q.c[d] * q.n, 0) / k) as V3);
  }
  if (!isFitted(info, name)) return [];
  const mine = instanceBox(box, info, name), c = at.get(name);
  return names.filter(n => n !== name && at.has(n) && (inCorners(mine, at.get(n)!) || (!!c && isFitted(info, n) && inCorners(instanceBox(box, info, n), c))));
}

/**
 * Merge instances into one: their points join `into` in every scan (made there, with its type and fit, where it
 * has none yet), keeping their part labels. The merged instances are left with no points, so they are gone.
 */
export function mergeInstances(scans: InstScan[], info: ObjInfo, into: string, from: string[]): OpOut {
  const bodies = new Map<string, OpBody[]>(), I = info.get(into), want = new Set(from);
  let n = 0;
  for (const s of scans) {
    const st = s.state, ids = new Set([...st.objects].filter(([, o]) => want.has(o.name)).map(([id]) => id));
    if (!ids.size) continue;
    const m = new Uint8Array(s.nv), byLabel = new Map<number, Uint8Array>();
    let k = 0;
    for (let v = 0; v < s.nv; v++) {
      const p = s.pidOfV[v];
      if (!ids.has(st.obj[p])) continue;
      m[v] = 1; k++;
      const l = st.label[p];
      if (l) (byLabel.get(l) ?? byLabel.set(l, new Uint8Array(s.nv)).get(l)!)[v] = 1;
    }
    if (!k) continue;
    const set = s.toSet(m), id = idsNamed(st.objects, into)[0];
    if (id) push(bodies, s.scan, { op: 'object.assign', obj: id, set } as OpBody);
    else {
      const nid = nextId(st.objects);
      if (nid >= MAX_ID) continue;
      push(bodies, s.scan, { op: 'object.new', obj: nid, name: into, set } as OpBody,
        ...(I?.of ? [{ op: 'object.of', obj: nid, of: I.of, ...(I.F ? { F: I.F } : {}) } as OpBody] : []));
    }
    // joining another object drops a point's label: give them back
    for (const [l, lm] of byLabel) push(bodies, s.scan, { op: 'label.assign', label: l, set: s.toSet(lm) } as OpBody);
    n += k;
  }
  const what = from.map(x => `"${x}"`).join(', ');
  return { bodies, note: n ? `merged ${what} into "${into}" (${n.toLocaleString()} points)` : `${what}: no points to merge` };
}
